/**
 * TranscriptView — read-only transcript embedded in SessionComplete and
 * CompletePage.
 *
 * Sprint 12.7 redesign: clearer speaker differentiation. Kaiva turns now
 * sit inside a paper card with a 3 px persimmon left-rail accent and an
 * asymmetric `rounded-tl-[4px]` corner. User turns mirror the chat
 * user-bubble pattern (slate-navy spotlight bubble with `rounded-tr-[4px]`
 * and linen text). System notes keep the dashed-pill pattern.
 *
 * The two bubble surfaces (paper-card vs slate-spotlight) plus the
 * left/right alignment make the speaker readable at a glance without
 * relying on the small avatar or speaker-label eyebrow.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Copy, Sparkles } from 'lucide-react';
import {
  normalizeTranscript,
  type NormalizedTranscriptEntry,
  type TranscriptInput,
} from '../lib/transcript';
import { KaivaAvatar } from './brand';
import { EmptyHint } from './feedback';

export interface TranscriptViewProps {
  transcript?: TranscriptInput;
  autoScroll?: boolean;
  title?: string;
}

export function TranscriptView({
  transcript,
  autoScroll = true,
  title = 'Transcript',
}: TranscriptViewProps) {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');
  const containerRef = useRef<HTMLDivElement | null>(null);

  const entries = useMemo(() => normalizeTranscript(transcript), [transcript]);

  useEffect(() => {
    if (!autoScroll || !containerRef.current || entries.length === 0) {
      return;
    }
    containerRef.current.scrollTop = containerRef.current.scrollHeight;
  }, [autoScroll, entries.length]);

  const copyTranscript = async () => {
    const text = entries
      .map((entry) => {
        const label =
          entry.speaker === 'assistant'
            ? 'KAIVA'
            : entry.speaker === 'user'
              ? 'YOU'
              : 'SYSTEM';
        const time = formatTime(entry.timestamp);
        return `[${time}] ${label}: ${entry.text}`;
      })
      .join('\n');

    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error('Clipboard is not available');
      }
      await navigator.clipboard.writeText(text);
      setCopyState('copied');
      window.setTimeout(() => setCopyState('idle'), 1500);
    } catch {
      setCopyState('error');
      window.setTimeout(() => setCopyState('idle'), 1500);
    }
  };

  const copyLabel =
    copyState === 'copied'
      ? 'Copied'
      : copyState === 'error'
        ? 'Copy failed'
        : 'Copy transcript';

  return (
    <section className="flex flex-col gap-3">
      <header className="flex items-center justify-between gap-3">
        <p
          className="kv-eyebrow kv-eyebrow--accent"
        >
          {title}
        </p>
        {entries.length > 0 && (
          <span
            className="kv-eyebrow tabular-nums"
            style={{
              color: 'hsl(var(--muted-foreground))',
            }}
          >
            {entries.length} turn{entries.length === 1 ? '' : 's'}
          </span>
        )}
      </header>

      <div
        ref={containerRef}
        className="max-h-[460px] overflow-y-auto"
        style={{
          background: 'hsl(var(--background))',
          borderRadius: 12,
          border: '1px solid hsl(var(--border-strong))',
          padding: '16px 18px',
        }}
      >
        {entries.length === 0 ? (
          <div className="py-6">
            <EmptyHint
              title="No transcript yet"
              body="Once the interview wraps, the conversation lands here."
            />
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {entries.map((entry) => (
              <TranscriptTurn key={entry.id} entry={entry} />
            ))}
          </div>
        )}
      </div>

      {entries.length > 0 && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={copyTranscript}
            className="inline-flex items-center gap-1.5 transition-colors"
            style={{
              height: 32,
              padding: '0 14px',
              borderRadius: 8,
              background: 'hsl(var(--teal))',
              color: 'hsl(var(--slate-navy))',
              border: '1px solid hsl(var(--teal))',
              fontSize: 12.5,
              fontWeight: 500,
            }}
          >
            <Copy className="h-3 w-3" aria-hidden="true" />
            {copyLabel}
          </button>
        </div>
      )}
    </section>
  );
}

function TranscriptTurn({ entry }: { entry: NormalizedTranscriptEntry }) {
  if (entry.speaker === 'system') {
    return (
      <div className="flex justify-center">
        <span
          className="inline-flex max-w-full items-center gap-1.5 rounded-full px-3 py-1 italic"
          style={{
            fontSize: 11.5,
            background: 'hsl(var(--muted) / 0.6)',
            color: 'hsl(var(--muted-foreground))',
            border: '1px dashed hsl(var(--border))',
          }}
        >
          <Sparkles className="h-2.5 w-2.5 flex-shrink-0" aria-hidden="true" />
          <span className="truncate">{entry.text}</span>
        </span>
      </div>
    );
  }

  const isKaiva = entry.speaker === 'assistant';
  const label = isKaiva ? 'KAIVA' : 'YOU';
  const time = formatTime(entry.timestamp);

  if (isKaiva) {
    return (
      <article className="flex flex-col items-start gap-1">
        <div className="flex items-center gap-2">
          <KaivaAvatar size={18} />
          <SpeakerLabel label={label} time={time} />
        </div>
        <div
          className="rounded-xl rounded-tl-[4px] px-3.5 py-2.5"
          style={{
            background: 'hsl(var(--card))',
            border: '1px solid hsl(var(--border-strong))',
            borderLeft: '3px solid hsl(var(--persimmon))',
            maxWidth: '88%',
          }}
        >
          <p style={transcriptBodyStyle}>{entry.text}</p>
        </div>
      </article>
    );
  }

  return (
    <article className="flex flex-col items-end gap-1">
      <SpeakerLabel label={label} time={time} align="right" />
      <div
        className="rounded-xl rounded-tr-[4px] px-3.5 py-2.5"
        style={{
          background: 'hsl(var(--slate-navy))',
          color: 'hsl(var(--linen))',
          maxWidth: '88%',
        }}
      >
        <p style={{ ...transcriptBodyStyle, color: 'hsl(var(--linen))' }}>
          {entry.text}
        </p>
      </div>
    </article>
  );
}

function SpeakerLabel({
  label,
  time,
  align = 'left',
}: {
  label: string;
  time: string;
  align?: 'left' | 'right';
}) {
  return (
    <div
      className="kv-eyebrow tabular-nums"
      style={{
        color: 'hsl(var(--muted-foreground))',
        textAlign: align,
      }}
    >
      <span style={{ fontWeight: 600, color: 'hsl(var(--foreground))' }}>{label}</span>
      <span style={{ fontWeight: 400 }}>
        {' · '}
        {time}
      </span>
    </div>
  );
}

const transcriptBodyStyle: React.CSSProperties = {
  fontSize: 13.5,
  lineHeight: 1.5,
  letterSpacing: '-0.003em',
  color: 'hsl(var(--foreground))',
  whiteSpace: 'pre-wrap',
  margin: 0,
};

function formatTime(isoTimestamp: string): string {
  const date = new Date(isoTimestamp);
  if (Number.isNaN(date.getTime())) return '--:--';
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

export default TranscriptView;
